/**
 * cnd-welcome-back.js
 *
 * Click handler + page bootstrapper for the cnd-welcome-back.liquid
 * section. Reads URL params client-side (URLSearchParams) and:
 *   - shows the personalised welcome view if the link is valid
 *   - shows the fallback view if variant / plan / discount are missing
 *   - binds the CTA so a click POSTs /cart/add.js with id, quantity,
 *     and selling_plan, then navigates to /discount/{code}?redirect=/cart
 *     so Shopify applies the welcome discount server-side.
 *
 * Why /discount/{code} instead of /cart?discount={code}:
 * /discount/{code} is the same endpoint the cart page's "Apply discount"
 * form posts to. It applies the discount server-side, which is more
 * reliable than the ?discount= URL param — the URL param has known
 * issues with customer-specific discount codes when the customer is
 * a guest (Shopify can't match the customer eligibility until email is
 * captured at checkout).
 *
 * Self-contained — does not depend on cnd-theme.js.
 */
(function () {
  'use strict';

  var CART_ADD_URL = '/cart/add.js';

  function addToCart(payload) {
    return fetch(CART_ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function text(el, value) {
    if (el && value != null && value !== '') el.textContent = value;
  }

  function show(el) {
    if (el) el.hidden = false;
  }

  function hide(el) {
    if (el) el.hidden = true;
  }

  function bootstrap(root) {
    var params = new URLSearchParams(window.location.search);

    var name = (params.get('name') || '').trim();
    var variantId = (params.get('variant') || '').trim();
    var planId = (params.get('plan') || '').trim();
    var cadence = (params.get('cadence') || '').trim();
    var discountCode = (params.get('discount') || '').trim();
    var productTitle = (params.get('product_title') || '').trim();
    var productHandle = (params.get('product') || '').trim();
    var imageUrl = (params.get('img') || '').trim();

    var quantityRaw = parseInt(params.get('qty') || '1', 10);
    var quantity = Number.isFinite(quantityRaw) && quantityRaw >= 1 ? quantityRaw : 1;

    var fallback = $('[data-welcome-back-fallback]', root);
    var content = $('[data-welcome-back-content]', root);

    if (!variantId || !planId || !discountCode) {
      hide(content);
      show(fallback);
      return;
    }

    text($('[data-wb-name]', root), name || 'there');
    text($('[data-wb-title]', root), productTitle || 'Your subscription');

    if (imageUrl) {
      var media = $('.welcome-back__card-media', root);
      var placeholder = $('[data-wb-placeholder]', root);
      if (media && placeholder) {
        var img = document.createElement('img');
        img.className = 'welcome-back__card-img';
        img.src = imageUrl;
        img.alt = productTitle || name || 'Product image';
        img.loading = 'eager';
        img.decoding = 'async';
        placeholder.replaceWith(img);
      }
    }

    if (cadence) {
      text($('[data-wb-cadence]', root), cadence);
      show($('[data-wb-cadence-row]', root));
    }
    if (quantity > 1) {
      text($('[data-wb-quantity]', root), quantity + ' per delivery');
      show($('[data-wb-quantity-row]', root));
    }
    text($('[data-wb-discount]', root), discountCode);
    show($('[data-wb-discount-row]', root));

    var cta = $('[data-welcome-back-cta]', root);
    if (cta) {
      cta.dataset.variantId = variantId;
      cta.dataset.sellingPlanId = planId;
      cta.dataset.discountCode = discountCode;
      cta.dataset.quantity = String(quantity);
    }

    document.title = 'Welcome back' + (name ? ', ' + name : '') + ' — Carbon Neutral Drinks';

    hide(fallback);
    show(content);

    if (cta) bindCta(cta, root);
  }

  function bindCta(btn, root) {
    var variantId = btn.dataset.variantId;
    var planId = btn.dataset.sellingPlanId;
    var discountCode = btn.dataset.discountCode;
    var quantity = parseInt(btn.dataset.quantity || '1', 10);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    var errorText = btn.dataset.errorText ||
      'Something went wrong. Please refresh and try again.';

    if (!variantId || !planId) return;

    var wrap = btn.parentElement;
    var note = wrap ? wrap.querySelector('.welcome-back__cta-note') : null;
    var errorEl = null;

    function showError(msg) {
      if (!wrap) return;
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'welcome-back__error';
        errorEl.setAttribute('role', 'alert');
        wrap.appendChild(errorEl);
      }
      errorEl.textContent = msg;
    }

    function setLoading(loading) {
      btn.disabled = loading;
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (loading && note) note.textContent = 'Adding to your cart…';
    }

    btn.addEventListener('click', function (event) {
      event.preventDefault();
      if (btn.disabled) return;

      setLoading(true);
      if (errorEl) errorEl.remove();

      addToCart({
        id: parseInt(variantId, 10),
        quantity: quantity,
        selling_plan: parseInt(planId, 10),
      })
        .then(function (r) {
          if (!r.ok) {
            throw new Error('Add to cart failed: ' + r.status);
          }
          if (discountCode) {
            // /discount/{code} is the cart page's own "Apply discount"
            // endpoint. Server-side apply is more reliable than the
            // ?discount= URL param for customer-specific codes.
            window.location.href =
              '/discount/' +
              encodeURIComponent(discountCode) +
              '?redirect=' +
              encodeURIComponent('/cart');
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function () {
          setLoading(false);
          showError(errorText);
        });
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-welcome-back]');
    roots.forEach(bootstrap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();